<template>
  <div class="dashboard-shell">
    <AppTopbar />
    <main class="page-shell">
      <div class="container-readable stack-lg">
        
        <!-- Hero Banner העליון מעוצב בצבעי המותג -->
        <section class="hero-banner">
          <div class="hero-container">
            <div class="hero-content">
              <router-link to="/" class="back-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                חזרה לאזור האישי
              </router-link>
              
              <h1 class="hero-title">עדכון מסלול</h1>
              <p class="hero-subtitle">בחרו מסלול מתאים ועדכנו אותו בצורה מהירה וברורה.</p>

              <div v-if="currentPlanName" class="current-chip">
                <span>המסלול הפעיל כרגע:</span>
                <strong>{{ currentPlanName }}</strong>
              </div>
            </div>
          </div>
        </section>

        <!-- פריסת העמוד: רשימת מסלולים + סיכום -->
        <section class="plan-layout">
          
          <!-- פאנל בחירת המסלולים -->
          <div class="surface card-block stack-md plan-list-panel">
            <div class="search-row">
              <input 
                v-model="searchTerm" 
                type="text" 
                class="input-control" 
                placeholder="חיפוש לפי שם מסלול או מחיר" 
              />
              <span class="muted count-pill">{{ filteredPlans.length }} תוצאות</span>
            </div>

            <p v-if="loadingPlans" class="muted">טוען מסלולים...</p>

            <div v-else-if="filteredPlans.length > 0" class="stack-md">
              <!-- תצוגת הכרטיסיות (Grid) -->
              <div class="results-grid">
                <button
                  v-for="plan in visibleResults" 
                  :key="plan.id" 
                  type="button"
                  class="plan-card" 
                  :class="{ selected: String(selectedPlanId) === String(plan.id) }"
                  @click="selectPlan(plan.id)"
                >
                  <div class="plan-card-header">
                    <span class="plan-card-title">{{ plan.name }}</span>
                    <span v-if="currentPlanName && plan.name === currentPlanName" class="active-label">
                      פעיל כרגע
                    </span>
                  </div>

                  <div class="plan-card-body">
                    <div class="plan-price-wrap" v-if="plan.price">
                      <span class="price-currency">₪</span>
                      <span class="price-num">{{ plan.price }}</span>
                      <span class="price-period">/חודש</span>
                    </div>

                    <div class="selection-badge" v-if="String(selectedPlanId) === String(plan.id)">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      מסלול נבחר
                    </div>
                  </div>
                </button>
              </div>

              <!-- כפתור להציג עוד -->
              <button 
                v-if="hasMoreResults" 
                type="button" 
                class="btn btn-ghost show-more-btn" 
                @click="showAllResults = !showAllResults"
              >
                {{ showAllResults ? 'להציג פחות' : 'להציג את כל התוצאות' }}
              </button>
            </div>

            <p v-else class="muted">לא נמצאו מסלולים התואמים לחיפוש.</p>
          </div>

          <!-- סרגל צד: סיכום בחירה -->
          <aside class="surface card-block stack-md plan-summary-panel">
            <h2 class="section-title">סיכום בחירה</h2>
            
            <div class="summary-box stack-sm">
              <p class="muted summary-key">מסלול נבחר</p>
              <p v-if="selectedPlan" class="summary-value">{{ selectedPlan.name }}</p>
              <p v-else class="summary-value muted">עדיין לא נבחר מסלול</p>
              
              <p v-if="selectedPlan && selectedPlan.price" class="muted">עלות חודשית: ₪{{ selectedPlan.price }}</p>
              
              <template v-if="isPlanChanging">
                <p class="muted">דמי שינוי תכנית: ₪{{ planChangeFee }}</p>
                <template v-if="planPriceDiff > 0">
                  <p class="muted">הפרש בין המסלולים: ₪{{ planPriceDiff }}/חודש</p>
                  <p class="muted">הפרש יחסי לשארית החודש: ₪{{ diffToCharge }}</p>
                </template>
                <p class="summary-value highlight-total">סה"כ לתשלום עכשיו: ₪{{ totalToCharge }}</p>
              </template>
              
              <p v-if="selectedPlan && currentPlanName && selectedPlan.name === currentPlanName" class="active-hint">
                זהו המסלול הפעיל כרגע
              </p>
            </div>

            <div class="actions-row">
              <button 
                class="btn btn-primary" 
                :disabled="!selectedPlanId || loadingChangePlan" 
                @click="confirmChangePlan"
              >
                <span v-if="!loadingChangePlan">עדכון מסלול</span>
                <span v-else>שומר...</span>
              </button>
              <router-link to="/" class="btn btn-ghost">חזרה ללא שינוי</router-link>
            </div>

            <div v-if="planError" class="error-inline">{{ planError }}</div>
          </aside>

        </section>
      </div>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import AppFooter from '@/components/AppFooter.vue'
import AppTopbar from '@/components/AppTopbar.vue'

const NO_TOKEN_MSG = 'לא נמצא אמצעי תשלום שמור. יש להוסיף כרטיס לפני התשלום.'

export default {
  name: 'ChangePlan',
  components: { AppTopbar, AppFooter },
  data() {
    return {
      loadingPlans: false,
      availablePlans: [],
      selectedPlanId: null,
      searchTerm: '',
      showAllResults: false,
      loadingChangePlan: false,
      planError: '',
      currentPlanName: '',
      currentPlanId: null,
    }
  },
  computed: {
    filteredPlans() {
      const term = this.searchTerm.trim().toLowerCase()
      const sorted = [...this.availablePlans].sort((a, b) => Number(a.price || 0) - Number(b.price || 0))
      if (!term) return sorted
      return sorted.filter(p => String(p.name || '').toLowerCase().includes(term) || String(p.price || '').includes(term))
    },
    visibleResults() { return this.showAllResults ? this.filteredPlans : this.filteredPlans.slice(0, 6) },
    hasMoreResults() { return this.filteredPlans.length > 6 },
    selectedPlan() { return this.availablePlans.find(p => String(p.id) === String(this.selectedPlanId)) || null },
    _planDiffBase() {
      if (!this.selectedPlan || !this.currentPlanId) return null
      if (String(this.selectedPlan.id) === String(this.currentPlanId)) return null
      const currentPlan = this.availablePlans.find(p => String(p.id) === String(this.currentPlanId))
      if (!currentPlan) return null
      const newPrice = parseFloat(this.selectedPlan.price || 0)
      const curPrice = parseFloat(currentPlan.price || 0)
      if (newPrice <= curPrice) return null
      return { diff: Math.round((newPrice - curPrice) * 100) / 100, newPrice, curPrice }
    },
    diffToCharge() {
      if (!this._planDiffBase) return 0
      const { diff } = this._planDiffBase
      const today = new Date()
      const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
      const daysLeft = daysInMonth - today.getDate()
      return Math.round((diff / daysInMonth) * daysLeft * 100) / 100
    },
    planPriceDiff() {
      return this._planDiffBase ? this._planDiffBase.diff : 0
    },
    isPlanChanging() {
      return !!(this.selectedPlan && this.currentPlanId && String(this.selectedPlan.id) !== String(this.currentPlanId))
    },
    planChangeFee() {
      return this.isPlanChanging ? 15 : 0
    },
    totalToCharge() {
      return Math.round((this.planChangeFee + this.diffToCharge) * 100) / 100
    },
  },
  mounted() {
    if (this.isNull(localStorage.getItem('user_token'))) { this.$router.push('/login'); return }
    this.fetchDashboard()
    this.fetchPlans()
  },
  methods: {
    fetchDashboard() {
      this.api({ action: 'get_my_dashboard_data', method: 'get' }, (response) => {
        if (response?.data?.success && response.data.plan) {
          this.currentPlanName = response.data.plan.name || ''
          this.currentPlanId   = response.data.plan.id   || null
          this.selectedPlanId  = response.data.plan.id   || null
        }
      })
    },
    fetchPlans() {
      this.loadingPlans = true
      this.api({ action: 'get_bestphone_plans', method: 'get' }, (response) => {
        this.availablePlans = response?.data?.plans || []
        if (!this.selectedPlanId && this.availablePlans.length) this.selectedPlanId = this.availablePlans[0].id
        this.loadingPlans = false
      }, () => { this.planError = 'אירעה שגיאה בטעינת המסלולים'; this.loadingPlans = false })
    },
    selectPlan(id) { this.selectedPlanId = id },
    confirmChangePlan() {
      if (!this.selectedPlanId) return
      this.loadingChangePlan = true; this.planError = ''
      this.api({ action: 'change_my_plan', data: { plan_id: this.selectedPlanId }, method: 'post' }, (response) => {
        if (response?.data?.success) { this.$router.push({ path: '/', query: { success: encodeURIComponent('המסלול עודכן בהצלחה') } }) }
        else if (response?.data?.error === 'no_payment_token') {
          this.$router.push({ path: '/payment-method', query: { error: encodeURIComponent(NO_TOKEN_MSG) } })
          this.loadingChangePlan = false
          return
        }
        else {
          const e = response?.data?.error
          const detail = response?.data?.detail
          this.planError =
            e === 'same_plan' ? 'אתם כבר נמצאים במסלול הזה. בחרו מסלול אחר כדי לעדכן.'
            : e === 'payment_failed' ? 'החיוב נכשל. בדקו את אמצעי התשלום ונסו שוב.'
            : e === 'no_active_number' ? 'לא נמצא מספר פעיל בחשבון.'
            : e === 'change_failed' ? ('לא הצלחנו לעדכן את המסלול מול הספק' + (detail ? ' (' + detail + ')' : '') + '. נסו שוב מאוחר יותר.')
            : 'לא הצלחנו לעדכן את המסלול. נסו שוב.'
        }
        this.loadingChangePlan = false
      }, () => { this.planError = 'לא הצלחנו לעדכן את המסלול.'; this.loadingChangePlan = false })
    },
  }
}
</script>

<style lang="scss" scoped src="@/styles/pages/change-plan.scss"></style>

